import React, { useState } from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQDropdown, { UQDropdownOption } from '../../components/dropdown';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const options: UQDropdownOption[] = [
  { label: 'Apple', value: 'apple' },
  { label: 'Banana', value: 'banana' },
  { label: 'Orange', value: 'orange' },
  { label: 'Grape', value: 'grape' },
  { label: 'Mango', value: 'mango', disabled: true },
];

const attributes = [
  { attribute: 'options', type: 'UQDropdownOption[]', default: '[]', description: 'Array of dropdown options.' },
  { attribute: 'label', type: 'string', default: 'undefined', description: 'Label for the dropdown.' },
  { attribute: 'name', type: 'string', default: 'undefined', description: 'Name of the dropdown.' },
  { attribute: 'placeholder', type: 'string', default: "'Select...'", description: 'Placeholder text.' },
  { attribute: 'appearance', type: "'outline' | 'underline' | 'filled-darker' | 'filled-lighter'", default: "'outline'", description: 'Dropdown appearance style.' },
  { attribute: 'size', type: "'small' | 'medium' | 'large'", default: "'medium'", description: 'Dropdown size.' },
  { attribute: 'multiselect', type: 'boolean', default: 'false', description: 'Enable multiselect mode.' },
  { attribute: 'clearable', type: 'boolean', default: 'false', description: 'Show a clear button to reset selection.' },
  { attribute: 'disabled', type: 'boolean', default: 'false', description: 'Disable the dropdown.' },
  { attribute: 'required', type: 'boolean', default: 'false', description: 'Mark the dropdown as required.' },
  { attribute: 'boldLabel', type: 'boolean', default: 'false', description: 'Make the label bold.' },
  { attribute: 'as', type: "'button'", default: "'button'", description: 'Root element type.' },
  { attribute: 'defaultSelectedOptions', type: 'string[]', default: 'undefined', description: 'Initial selection for uncontrolled dropdown.' },
  { attribute: 'selectedOptions', type: 'string[]', default: 'undefined', description: 'Controlled selected option(s).' },
  { attribute: 'onOptionSelect', type: '(event, data) => void', default: 'undefined', description: 'Callback when an option is selected.' },
  { attribute: 'onActiveOptionChange', type: '(data) => void', default: 'undefined', description: 'Callback when the active option changes.' },
  { attribute: 'mountNode', type: 'HTMLElement | { element?: HTMLElement | null; className?: string } | null', default: 'undefined', description: 'Where the dropdown popup is mounted.' },
  { attribute: 'defaultOpen', type: 'boolean', default: 'false', description: 'Initial open state (uncontrolled).' },
  { attribute: 'open', type: 'boolean', default: 'undefined', description: 'Controlled open state.' },
  { attribute: 'onOpenChange', type: '(e, data) => void', default: 'undefined', description: 'Callback when open/closed state changes.' },
  { attribute: 'disableAutoFocus', type: 'boolean', default: 'false', description: 'Disable auto-focus on first item.' },
  { attribute: 'inlinePopup', type: 'boolean', default: 'false', description: 'Render popup inline in the DOM.' },
  { attribute: 'error', type: 'string', default: 'undefined', description: 'Error message to display.' },
  { attribute: 'expandIcon', type: 'React.ReactNode', default: 'undefined', description: 'Custom expand arrow icon.' },
  { attribute: 'clearButton', type: 'React.ReactNode', default: 'undefined', description: 'Custom clear button icon.' },
  { attribute: 'positioning', type: 'any', default: 'undefined', description: 'Popup positioning config.' },
];

const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
  { columnId: 'attribute', renderHeaderCell: () => 'Attribute', renderCell: (item) => item.attribute },
  { columnId: 'type', renderHeaderCell: () => 'Type', renderCell: (item) => <code>{item.type}</code> },
  { columnId: 'default', renderHeaderCell: () => 'Default', renderCell: (item) => <code>{item.default}</code> },
  { columnId: 'description', renderHeaderCell: () => 'Description', renderCell: (item) => item.description },
];

const DropdownDemoPage = () => {
  const [selected, setSelected] = useState<string[]>([]);
  const [multiSelected, setMultiSelected] = useState<string[]>(['banana', 'grape']);
  const [open, setOpen] = useState(false);

  const codeExample = `
import React, { useState } from 'react';
import { UQDropdown } from '@uniqconsulting/ui';

const options = [
  { label: 'Apple', value: 'apple' },
  { label: 'Banana', value: 'banana' },
  { label: 'Orange', value: 'orange' },
];

const [selected, setSelected] = useState<string[]>([]);

<UQDropdown
  label="Fruit"
  name="fruit"
  options={options}
  selectedOptions={selected}
  onOptionSelect={(_, data) => setSelected([data.value])}
/>
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Dropdown']} />
      <h1>UQDropdown Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQDropdown</code> component is a flexible dropdown/select input that supports single and multi-select, clearable values, custom icons, controlled and uncontrolled modes, and full accessibility. It is styled to match <code>UQInput</code> and supports advanced features such as custom popup mounting, appearance variants, and more.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
        rowLines
      />
      <UQSpacer size="m" />

      {/* Examples */}
      <h2>Examples</h2>
      <UQSpacer size="s" />

      <h3>Single Select (Controlled)</h3>
      <UQDropdown
        label="Fruit"
        name="fruit"
        options={options}
        selectedOptions={selected}
        onOptionSelect={(_, data) => setSelected([data.value])}
        placeholder="Choose a fruit"
        clearable
      />
      <UQSpacer size="m" />

      <h3>Multi Select (Controlled)</h3>
      <UQDropdown
        label="Fruits"
        name="fruits"
        options={options}
        multiselect
        selectedOptions={multiSelected}
        onOptionSelect={(_, data) => {
          setMultiSelected((prev) =>
            prev.includes(data.value)
              ? prev.filter((v) => v !== data.value)
              : [...prev, data.value]
          );
        }}
        clearable
        placeholder="Choose fruits"
      />
      <UQSpacer size="m" />

      <h3>Uncontrolled (defaultSelectedOptions)</h3>
      <UQDropdown
        label="Uncontrolled"
        name="uncontrolled"
        options={options}
        defaultSelectedOptions={['orange']}
        clearable
      />
      <UQSpacer size="m" />

      <h3>Custom Appearance & Size</h3>
      <UQDropdown
        label="Underline Large"
        name="underline"
        options={options}
        appearance="underline"
        size="large"
        placeholder="Pick a fruit"
      />
      <UQSpacer size="m" />

      <h3>Disabled & Error</h3>
      <UQDropdown
        label="Disabled"
        name="disabled"
        options={options}
        disabled
        placeholder="Can't select"
      />
      <UQSpacer size="s" />
      <UQDropdown
        label="With Error"
        name="error"
        options={options}
        error="Selection required"
      />
      <UQSpacer size="m" />

      <h3>Custom Expand/Clear Icons</h3>
      <UQDropdown
        label="Custom Icons"
        name="customicons"
        options={options}
        clearable
        expandIcon={<span style={{ color: 'purple' }}>▼</span>}
        clearButton={<span style={{ color: 'red', fontWeight: 'bold' }}>✖</span>}
      />
      <UQSpacer size="m" />

      {/* Code Example */}
      <h2>Code Example</h2>
      <UQSpacer size="s" />
      <UQCode code={codeExample} enableCopy />
    </>
  );
};

export default DropdownDemoPage;